import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  createScenarioFromPreset,
  createWorkflowGraphScenario,
  duplicateScenario,
  getAutomationsQueryKey,
  getScenarioQueryKey,
  patchScenario,
  type PatchScenarioPayload,
  type ScenarioDetail,
} from '../api/automations.client.js';

import type { CreateWorkflowGraphScenarioPayload } from '../api/automations.client.js';

export function usePatchScenario() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  return useMutation<
    ScenarioDetail,
    Error,
    { id: string; payload: PatchScenarioPayload },
    { previousDetail: ScenarioDetail | undefined }
  >({
    mutationFn: ({ id, payload }) => patchScenario(id, payload),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: getScenarioQueryKey(workspaceId, id) });
      const previousDetail = queryClient.getQueryData<ScenarioDetail>(
        getScenarioQueryKey(workspaceId, id),
      );
      if (previousDetail) {
        queryClient.setQueryData<ScenarioDetail>(getScenarioQueryKey(workspaceId, id), {
          ...previousDetail,
          ...(payload.name !== undefined ? { name: payload.name } : {}),
          ...(payload.description !== undefined ? { description: payload.description } : {}),
          ...(payload.triggerType !== undefined ? { triggerType: payload.triggerType } : {}),
          ...(payload.config !== undefined ? { config: payload.config } : {}),
          ...(payload.isActive !== undefined ? { isActive: payload.isActive } : {}),
        });
      }
      return { previousDetail };
    },
    onError: (_error, vars, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(getScenarioQueryKey(workspaceId, vars.id), context.previousDetail);
      }
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: getScenarioQueryKey(workspaceId, vars.id) });
      void queryClient.invalidateQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
    },
  });
}

export function useCreateFromPreset() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  return useMutation<ScenarioDetail, Error, { presetKey: string; name?: string }>({
    mutationFn: ({ presetKey, name }) => createScenarioFromPreset(presetKey, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
    },
  });
}

export function useCreateWorkflowGraphScenario() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  return useMutation<ScenarioDetail, Error, CreateWorkflowGraphScenarioPayload>({
    mutationFn: createWorkflowGraphScenario,
    onSuccess: (created) => {
      queryClient.setQueryData(getScenarioQueryKey(workspaceId, created.id), created);
      void queryClient.invalidateQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
    },
  });
}

export function useDuplicateScenario() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  return useMutation<ScenarioDetail, Error, string>({
    mutationFn: (id) => duplicateScenario(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
    },
  });
}
